import * as InventoryActsActions from './inventory-act.actions';

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {InventoryAct, InventoryActSearchParams} from '@accounting/_common/models/inventory-act.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const inventoryActFeatureKey = 'inventoryActs';

export function sortByDate(a: InventoryAct, b: InventoryAct) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<InventoryAct> = createEntityAdapter<InventoryAct>({
    sortComparer: sortByDate
});

export type InventoryActsState = EntityStateModel<InventoryAct, InventoryActSearchParams>;

export const initialState: InventoryActsState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const inventoryActsReducer = createReducer(
    initialState,

    on(InventoryActsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(InventoryActsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(InventoryActsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(InventoryActsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(InventoryActsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(InventoryActsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(InventoryActsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(InventoryActsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(InventoryActsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(InventoryActsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(InventoryActsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(InventoryActsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(InventoryActsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: InventoryActSearchParams | undefined, item: InventoryAct): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(item.date))) {
        return false;
    }

    if (searchParams.to && dayjs(item.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    return !(!item.comment && searchParams.query || searchParams.query && (item.comment as string).toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1);
};
